import { AudioTrack } from '../components/SoundGenerator/types';
import { getApiUrl } from '../config/api';

export interface SoundtrackExportData {
  projectId: string;
  mixedAudioUrl: string;
  trackData: {
    trackCount: number;
    masterVolume: number;
    tracks: Array<{
      name: string;
      volume: number;
      startTime: number;
      duration: number;
      isVideo?: boolean; // Backend expects this field
      trimStart?: number; // Track trim start time in seconds
      trimEnd?: number;   // Track trim end time in seconds
    }>;
  };
}

export interface SoundtrackStatus {
  hasSoundtrack: boolean;
  status: 'none' | 'processing' | 'completed' | 'failed';
  jobId?: string; // Track the current job ID
  soundtrackVideoUrl?: string;
  mixedAudioUrl?: string;
  trackCount?: number;
  masterVolume?: number;
  createdAt?: string;
  completedAt?: string;
  jobStatus?: {
    jobId: string;
    status: string;
    percentage: number;
    stage: string;
    message: string;
  };
}

export class SoundtrackService {
  private getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      ...(localStorage.getItem('authToken') && {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      }),
    };
  }

  async exportSoundtrackVideo(data: SoundtrackExportData) {
    const response = await fetch(getApiUrl('/video/mix-soundtrack'), {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to start soundtrack export: ${response.statusText}`);
    }

    return response.json();
  }

  async getSoundtrackStatus(projectId: string): Promise<SoundtrackStatus> {
    const response = await fetch(getApiUrl(`/video/soundtrack-status/${projectId}`), {
      headers: {
        ...(localStorage.getItem('authToken') && {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        })
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return { hasSoundtrack: false, status: 'none' };
      }
      throw new Error(`Failed to get soundtrack status: ${response.statusText}`);
    }

    return response.json();
  }

  prepareSoundtrackData(
    projectId: string, 
    mixedAudioUrl: string, 
    tracks: AudioTrack[], 
    masterVolume: number
  ): SoundtrackExportData {
    console.log('📦 prepareSoundtrackData - BEFORE MAPPING:');
    tracks.forEach((track, index) => {
      console.log(`  Track ${index + 1}:`, {
        name: track.name,
        duration: track.duration,
        originalDuration: track.originalDuration,
        trimStart: track.trimStart,
        trimEnd: track.trimEnd,
        startTime: track.startTime,
        volume: track.volume,
        isMuted: track.isMuted,
        isVideo: track.isVideo
      });
    });
    
    const mappedTracks = tracks.map(track => ({
      name: track.name,
      volume: track.volume,
      startTime: track.startTime,
      duration: track.duration,
      isVideo: track.isVideo || false,
      trimStart: track.trimStart || 0,
      trimEnd: track.trimEnd || track.duration
    }));
    
    console.log('📦 prepareSoundtrackData - AFTER MAPPING:');
    mappedTracks.forEach((track, index) => {
      console.log(`  Track ${index + 1}:`, track);
    });
    
    return {
      projectId,
      mixedAudioUrl,
      trackData: {
        trackCount: tracks.length,
        masterVolume,
        tracks: mappedTracks
      }
    };
  }
}
